import { CheckCircle2Icon, LandmarkIcon } from 'lucide-react';
import type { StripePaymentOutcome } from '@bcecloud/portal-ui';

/**
 * The no-login payment page's frame, shared by the single-event and series
 * views: BCE's palette, the Stripe Checkout layout, and the receipt.
 */

/**
 * BCE's palette for this page, since no tenant brand applies — the same
 * values as portal-ui's bce-theme.css: the portal-ui brand variables plus
 * the shadcn `--primary`/`--ring` triplets the buttons and focus rings read.
 */
export const BCE_PAGE_BRAND = {
  '--portal-brand-primary': '#1851d1',
  '--portal-brand-sidebar': '#1851d1',
  '--portal-brand-accent': '#ffc72c',
  '--portal-brand-link': '#1851d1',
  // Tailwind's theme resolves these once at the root, so they're set here too.
  '--color-brand': '#1851d1',
  '--color-brand-sidebar': '#1851d1',
  '--color-brand-accent': '#ffc72c',
  '--color-brand-link': '#1851d1',
  '--primary': '221.5 79.4% 45.7%',
  '--ring': '221.5 79.4% 45.7%',
} as React.CSSProperties;

/** BCE's main line (hq/brand/CLAUDE.md). */
export const BCE_PHONE = '(800) 849-8326';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const money = (value: number): string => USD.format(value);

/** Payment choices beyond the schedule's own lines. */
export const CHOICE = { total: 'total', custom: 'custom' } as const;
/** Stripe's card minimum is $0.50; the API asks for at least $1. */
export const MIN_PAYMENT = 1;
/** Loose shape check; the API validates the address properly. */
export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function longDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** A typed dollar amount, or null when it isn't one. */
export function parseAmount(text: string): number | null {
  const amount = Math.round(Number(text.replace(/[$,\s]/g, '')) * 100) / 100;
  return text.trim() && Number.isFinite(amount) ? amount : null;
}

/** The official white wordmark (hq/brand/CLAUDE.md): only ever on the royal panel. */
export const BCE_LOGO_WHITE = '/images/bce-logo-white.png';
export const BCE_LOGO_ALT = 'Best Corporate Events & TeamBuilding';

/**
 * The Stripe Checkout layout: a royal summary panel beside a white payment
 * panel on wide screens, stacked on phones. The panel is royal because the
 * brand's cataloged logo is the white wordmark, which never sits on light.
 */
export function Checkout({
  summary,
  children,
}: {
  readonly summary: React.ReactNode;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <main style={BCE_PAGE_BRAND} className="grid min-h-screen bg-white lg:grid-cols-2">
      <section className="bg-brand px-6 py-8 text-white sm:px-10 lg:flex lg:justify-end lg:py-16">
        <div className="w-full lg:max-w-[440px] lg:pr-12">
          <img src={BCE_LOGO_WHITE} alt={BCE_LOGO_ALT} className="h-8 w-auto" />
          <div className="mt-10">{summary}</div>
        </div>
      </section>
      <section className="px-6 py-8 sm:px-10 lg:py-16">
        <div className="w-full lg:max-w-[440px] lg:pl-12">
          {children}
          <p className="mt-10 text-portal-micro text-portal-muted">
            Questions about this balance? Reply to the email this link came in, or call{" "}
            <span className="whitespace-nowrap">{BCE_PHONE}</span>.
          </p>
        </div>
      </section>
    </main>
  );
}

/** A label and amount on the royal panel. */
export function SummaryRow({ label, value, strong = false }: { readonly label: string; readonly value: string; readonly strong?: boolean }): React.ReactElement {
  return (
    <div className={`flex items-baseline justify-between gap-4 py-3 ${strong ? 'font-bold text-white' : 'text-white/80'}`}>
      <dt className="text-portal-small">{label}</dt>
      <dd className="text-portal-small whitespace-nowrap">{value}</dd>
    </div>
  );
}

/** After paying: a card payment is done; a bank payment is on its way. */
export function Receipt({ outcome, amount, email }: { readonly outcome: StripePaymentOutcome; readonly amount: number; readonly email: string }): React.ReactElement {
  const bank = outcome === 'processing';
  return (
    <div>
      {bank ? <LandmarkIcon aria-hidden className="size-8 text-brand" /> : <CheckCircle2Icon aria-hidden className="size-8 text-portal-success" />}
      <h1 className="mt-3 text-[24px] leading-tight font-bold text-portal-ink">{bank ? 'Bank payment on its way' : 'Payment received'}</h1>
      <p className="mt-2 text-portal-small text-portal-muted">
        {bank
          ? `Your ${money(amount)} bank payment is processing. Bank payments usually clear in about four business days; we'll apply it as soon as it does.`
          : `Thank you. We received your ${money(amount)} payment.`}{' '}
        A receipt is on its way to {email}.
      </p>
    </div>
  );
}

/** Nothing left to pay. */
export function PaidInFull({ message }: { readonly message: string }): React.ReactElement {
  return (
    <>
      <CheckCircle2Icon aria-hidden className="size-8 text-portal-success" />
      <h1 className="mt-3 text-[24px] leading-tight font-bold text-portal-ink">Paid in full</h1>
      <p className="mt-2 text-portal-small text-portal-muted">{message}</p>
    </>
  );
}

/** A bank payment started through the link that hasn't cleared. */
export function ProcessingNotice({ since }: { readonly since: string | null }): React.ReactElement | null {
  if (!since) return null;
  return (
    <p className="mb-6 rounded-portal-button bg-portal-bg p-3 text-portal-small text-portal-muted">
      A bank payment started on {longDate(since)} is still clearing. It isn't counted in the balance yet.
    </p>
  );
}
